"use client";

import { Globe, RefreshCw, Search, LayoutTemplate, MapPin, Zap, Settings } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Expertise() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const services = [{
    index: "01",
    title: "Website Erstellung",
    description: "Individuelle, performante Websites, die Ihre Marke digital erlebbar machen und Kunden überzeugen.",
    Icon: Globe,
    linkText: "Webdesign-Leistungen ansehen"
  }, {
    index: "02",
    title: "Website Redesign",
    description: "Wir modernisieren Ihre bestehende Präsenz für ein zeitgemäßes Design und bessere Konversionsraten.",
    Icon: RefreshCw,
    linkText: "Website-Optimierung entdecken"
  }, {
    index: "03",
    title: "SEO Betreuung",
    description: "Sichtbarkeit steigern durch gezielte Optimierung, damit Sie von Ihren Kunden in Oldenburg gefunden werden.",
    Icon: Search,
    linkText: "SEO-Services erkunden"
  }, {
    index: "04",
    title: "Landingpages",
    description: "Hochkonvertierende Einzelseiten, die Besucher gezielt zu Anfragen oder Käufen führen.",
    Icon: LayoutTemplate,
    linkText: "Anfragen →"
  }, {
    index: "05",
    title: "Lokale Unternehmenswebsites",
    description: "Stärken Sie Ihre regionale Präsenz mit einer Website, die perfekt auf Ihren Standort zugeschnitten ist.",
    Icon: MapPin,
    linkText: "Anfragen →"
  }, {
    index: "06",
    title: "Technische Optimierung",
    description: "Maximale Geschwindigkeit und Sicherheit für ein reibungsloses Nutzererlebnis auf allen Endgeräten.",
    Icon: Zap,
    linkText: "Anfragen →"
  }, {
    index: "07",
    title: "Wartung und Pflege",
    description: "Wir halten Ihre Website technisch aktuell, sicher und performant, damit Sie sich auf Ihr Geschäft konzentrieren können.",
    Icon: Settings,
    linkText: "Anfragen →"
  }];
  return <section data-section-id="3992" id="unsere-expertise" className="bg-background text-foreground relative overflow-hidden py-24 md:py-32 px-6 md:px-12 lg:px-20">
      <div ref={headerRef} className={`max-w-[1400px] mx-auto mb-20 md:mb-28 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="font-mono text-xs md:text-sm tracking-widest text-muted-foreground mb-8 md:mb-12 uppercase">
          02 — Leistungen
        </div>
        <h2 className="font-default font-bold tracking-tight leading-[0.95] text-[44px] sm:text-[64px] md:text-[80px] lg:text-[88px] uppercase">
          Digitale Lösungen
          <br />
          für Ihren <span style={{
          fontStyle: "italic",
          color: "var(--primary)"
        }}>Erfolg</span>
        </h2>
      </div>

      <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
        {services.map((service, i) => {
        const Icon = service.Icon;
        const isMiddleColumn = i % 3 === 1;
        return <ServiceCard key={service.index} data-index={i} index={service.index} title={service.title} description={service.description} Icon={Icon} isMiddleColumn={isMiddleColumn} delay={i * 100} linkText={service.linkText} />;
      })}
      </div>
    </section>;
}
function ServiceCard({
  index,
  title,
  description,
  Icon,
  isMiddleColumn,
  delay,
  linkText,
  ...rest
}: {
  index: string;
  title: string;
  description: string;
  Icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  isMiddleColumn: boolean;
  delay: number;
  linkText: string;
  [key: string]: unknown;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <div ref={ref} {...rest} className={`group relative transition-all duration-1000 ease-out ${isMiddleColumn ? "md:mt-[60px]" : ""} ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
    transitionDelay: `${delay}ms`
  }}>
      <div className="relative p-8 md:p-10 transition-all duration-500 ease-out group-hover:-translate-y-1.5 border" style={{
      backgroundColor: "var(--card)",
      borderColor: "var(--border)",
      boxShadow: "var(--shadow-md)"
    }} onMouseEnter={e => {
      e.currentTarget.style.borderColor = "var(--primary)";
    }} onMouseLeave={e => {
      e.currentTarget.style.borderColor = "var(--border)";
    }}>
        <div className="flex items-start justify-between mb-12 md:mb-16">
          <div className="font-default font-bold text-[56px] md:text-[64px] leading-none tracking-tight transition-transform duration-500 group-hover:scale-105 origin-left" style={{
          color: "var(--primary)"
        }}>
            {index}
          </div>
          <Icon className="w-6 h-6 text-muted-foreground" strokeWidth={1} />
        </div>

        <Label className="block font-default font-semibold text-2xl md:text-[28px] leading-tight tracking-tight mb-5 text-foreground">
          {title}
        </Label>

        <p className="font-default text-[15px] leading-[1.6] text-muted-foreground mb-10 line-clamp-3">
          {description}
        </p>

        <Link to="/ContactPage" className="inline-block group/link">
          <Button variant="link" className="px-0 h-auto font-mono text-xs tracking-[0.2em] uppercase text-foreground hover:no-underline relative" style={{
          color: "var(--foreground)"
        }}>
            <span className="relative pb-1">
              {linkText}
              <span className="absolute left-0 right-0 bottom-0 h-px transition-all duration-500 group-hover/link:bg-[var(--primary)]" style={{
              backgroundColor: "var(--border)"
            }} />
            </span>
          </Button>
        </Link>
      </div>
    </div>;
}